/**
 * API Error Types
 * 
 * Standardized error codes and error interface for the Dominion Mandate Platform.
 * All API errors should use these standardized codes for consistent error handling.
 */

// ============================================================================
// Error Code Enum
// ============================================================================

/**
 * Standard API error codes
 * 
 * Error codes are categorized into logical groups for easier error handling
 * and consistent error messaging across the platform.
 */
export enum ApiErrorCode {
  // ============================================================================
  // Authentication & Authorization Errors (4xx)
  // ============================================================================
  
  /** User is not authenticated */
  UNAUTHORIZED = 'UNAUTHORIZED',
  
  /** User does not have permission to perform the action */
  FORBIDDEN = 'FORBIDDEN',
  
  /** Authentication token has expired */
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  
  /** Session has expired */
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  
  /** Invalid email or password credentials */
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  
  // ============================================================================
  // Validation Errors (4xx)
  // ============================================================================
  
  /** Request validation failed */
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  
  /** Required field is missing */
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  
  /** Field format is invalid */
  INVALID_FORMAT = 'INVALID_FORMAT',
  
  /** Authentication token is invalid */
  INVALID_TOKEN = 'INVALID_TOKEN',
  
  /** Invite code format is invalid */
  INVALID_INVITE_CODE = 'INVALID_INVITE_CODE',
  
  // ============================================================================
  // Resource Errors (4xx/409)
  // ============================================================================
  
  /** Requested resource was not found */
  NOT_FOUND = 'NOT_FOUND',
  
  /** Resource already exists */
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  
  /** Resource conflict (e.g., duplicate entry) */
  CONFLICT = 'CONFLICT',
  
  /** Resource not found (alternative naming) */
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  
  // ============================================================================
  // Operation Errors (4xx)
  // ============================================================================
  
  /** Failed to create resource */
  CREATE_FAILED = 'CREATE_FAILED',
  
  /** Failed to update resource */
  UPDATE_FAILED = 'UPDATE_FAILED',
  
  /** Failed to delete resource */
  DELETE_FAILED = 'DELETE_FAILED',
  
  /** Failed to fetch resource */
  FETCH_FAILED = 'FETCH_FAILED',
  
  /** Failed to upload resource */
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  
  // ============================================================================
  // Business Logic Errors (4xx)
  // ============================================================================
  
  /** Failed to join stream or event */
  JOIN_FAILED = 'JOIN_FAILED',
  
  /** User lacks required permissions */
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  
  /** Stream is not currently live */
  STREAM_NOT_LIVE = 'STREAM_NOT_LIVE',
  
  /** User has already joined */
  ALREADY_JOINED = 'ALREADY_JOINED',
  
  /** Invite is required to access resource */
  INVITE_REQUIRED = 'INVITE_REQUIRED',
  
  /** Maximum number of streams reached */
  MAX_STREAMS_REACHED = 'MAX_STREAMS_REACHED',
  
  /** Stream is already in progress */
  STREAM_IN_PROGRESS = 'STREAM_IN_PROGRESS',
  
  /** Invalid state transition attempted */
  INVALID_STATE_TRANSITION = 'INVALID_STATE_TRANSITION',
  
  /** Rate limit exceeded */
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  
  /** Region restriction applies */
  REGION_RESTRICTED = 'REGION_RESTRICTED',
  
  /** User account is banned */
  USER_BANNED = 'USER_BANNED',
  
  /** User email is not verified */
  USER_NOT_VERIFIED = 'USER_NOT_VERIFIED',
  
  // ============================================================================
  // Invite-Specific Errors (4xx)
  // ============================================================================
  
  /** Invite code is invalid */
  INVALID_CODE = 'INVALID_CODE',
  
  /** Invite code has expired */
  EXPIRED_CODE = 'EXPIRED_CODE',
  
  /** Invite code usage limit exceeded */
  CODE_LIMIT_EXCEEDED = 'CODE_LIMIT_EXCEEDED',
  
  /** Invite code has already been used */
  CODE_ALREADY_USED = 'CODE_ALREADY_USED',
  
  // ============================================================================
  // System Errors (5xx)
  // ============================================================================
  
  /** Feature not yet implemented */
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',
  
  /** Internal server error */
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  
  /** Database operation failed */
  DATABASE_ERROR = 'DATABASE_ERROR',
  
  /** External service call failed */
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  
  /** Service temporarily unavailable */
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
}

// ============================================================================
// Error Interface
// ============================================================================

/**
 * API Error interface
 * 
 * Standard structure for all API errors
 */
export interface ApiError {
  /** Standardized error code */
  code: ApiErrorCode | string;
  /** Human-readable error message */
  message: string;
  /** Additional error details (validation errors, field-specific errors, etc.) */
  details?: Record<string, unknown> | unknown[];
}

/**
 * Validation error details
 */
export interface ValidationErrorDetails {
  /** Field path that failed validation */
  field: string;
  /** The value that was provided */
  value?: unknown;
  /** Validation rule that failed */
  constraint: string;
  /** Human-readable message */
  message: string;
}

/**
 * Create a validation error with field-level details
 */
export function createValidationError(
  field: string,
  message: string,
  constraint: string,
  value?: unknown
): ApiError {
  return {
    code: ApiErrorCode.VALIDATION_ERROR,
    message: `Validation failed for field '${field}': ${message}`,
    details: {
      field,
      value,
      constraint,
      message,
    } as Record<string, unknown>,
  };
}

// ============================================================================
// Error Message Helpers
// ============================================================================

/**
 * Get default error messages for common error codes
 */
export function getDefaultErrorMessage(code: ApiErrorCode): string {
  const messages: Record<ApiErrorCode, string> = {
    [ApiErrorCode.UNAUTHORIZED]: 'Authentication required. Please sign in.',
    [ApiErrorCode.FORBIDDEN]: 'You do not have permission to perform this action.',
    [ApiErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please sign in again.',
    [ApiErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please sign in again.',
    [ApiErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password.',
    [ApiErrorCode.VALIDATION_ERROR]: 'The provided data is invalid.',
    [ApiErrorCode.MISSING_REQUIRED_FIELD]: 'A required field is missing.',
    [ApiErrorCode.INVALID_FORMAT]: 'The format of the provided data is invalid.',
    [ApiErrorCode.INVALID_TOKEN]: 'The authentication token is invalid.',
    [ApiErrorCode.INVALID_INVITE_CODE]: 'The invite code is invalid.',
    [ApiErrorCode.NOT_FOUND]: 'The requested resource was not found.',
    [ApiErrorCode.ALREADY_EXISTS]: 'This resource already exists.',
    [ApiErrorCode.CONFLICT]: 'A conflict occurred with the current state.',
    [ApiErrorCode.RESOURCE_NOT_FOUND]: 'The requested resource was not found.',
    [ApiErrorCode.CREATE_FAILED]: 'Failed to create the resource.',
    [ApiErrorCode.UPDATE_FAILED]: 'Failed to update the resource.',
    [ApiErrorCode.DELETE_FAILED]: 'Failed to delete the resource.',
    [ApiErrorCode.FETCH_FAILED]: 'Failed to fetch the resource.',
    [ApiErrorCode.UPLOAD_FAILED]: 'Failed to upload the resource.',
    [ApiErrorCode.JOIN_FAILED]: 'Failed to join the stream.',
    [ApiErrorCode.PERMISSION_DENIED]: 'You do not have permission to perform this action.',
    [ApiErrorCode.STREAM_NOT_LIVE]: 'The stream is not currently live.',
    [ApiErrorCode.ALREADY_JOINED]: 'You have already joined this stream.',
    [ApiErrorCode.INVITE_REQUIRED]: 'An invite code is required to access this resource.',
    [ApiErrorCode.MAX_STREAMS_REACHED]: 'You have reached the maximum number of allowed streams.',
    [ApiErrorCode.STREAM_IN_PROGRESS]: 'A stream is already in progress.',
    [ApiErrorCode.INVALID_STATE_TRANSITION]: 'This action is not allowed in the current state.',
    [ApiErrorCode.RATE_LIMIT_EXCEEDED]: 'Too many requests. Please try again later.',
    [ApiErrorCode.REGION_RESTRICTED]: 'This content is not available in your region.',
    [ApiErrorCode.USER_BANNED]: 'Your account has been banned.',
    [ApiErrorCode.USER_NOT_VERIFIED]: 'Please verify your email address.',
    [ApiErrorCode.INVALID_CODE]: 'The invite code is invalid.',
    [ApiErrorCode.EXPIRED_CODE]: 'The invite code has expired.',
    [ApiErrorCode.CODE_LIMIT_EXCEEDED]: 'This invite code has reached its usage limit.',
    [ApiErrorCode.CODE_ALREADY_USED]: 'This invite code has already been used.',
    [ApiErrorCode.NOT_IMPLEMENTED]: 'This feature is not yet implemented.',
    [ApiErrorCode.INTERNAL_ERROR]: 'An internal error occurred.',
    [ApiErrorCode.DATABASE_ERROR]: 'A database error occurred.',
    [ApiErrorCode.EXTERNAL_SERVICE_ERROR]: 'An external service is temporarily unavailable.',
    [ApiErrorCode.SERVICE_UNAVAILABLE]: 'The service is temporarily unavailable.',
  };
  
  return messages[code] ?? 'An unexpected error occurred.';
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if a string is a valid ApiErrorCode
 */
export function isApiErrorCode(value: string): value is ApiErrorCode {
  return Object.values(ApiErrorCode).includes(value as ApiErrorCode);
}
